import {
    useState,
    ReactNode,
    Dispatch,
    SetStateAction,
    useEffect,
    createElement,
    JSXElementConstructor,
} from 'react';
import { DialogProps, UseDialogOKHandler } from '../../hooks/useDialog';
import { Modal } from './Modal';

interface ActiveAsyncDialog {
    content: ReactNode;
    onCancel: () => void;
}

let portalSetDialog: Dispatch<
    SetStateAction<ActiveAsyncDialog | undefined>
> | null;

export type AsyncDialogResult<Output> =
    | {
          ok: false;
      }
    | {
          ok: true;
          data: Output;
      };

export function openAsyncDialog<Input, Output>(
    dialog: JSXElementConstructor<DialogProps<Input, Output>>,
    options: Input
) {
    return new Promise<AsyncDialogResult<Output>>((resolve, reject) => {
        if (!portalSetDialog)
            return reject(
                new Error(
                    'The AsyncDialogPortal needs to be in the rendering tree for this to be used!'
                )
            );

        let settled = false;
        const close = (result: AsyncDialogResult<Output>) => {
            if (settled) return;
            settled = true;
            portalSetDialog?.(undefined);
            resolve(result);
        };
        const onCancel = () => close({ ok: false });
        const props: DialogProps<Input, Output> = {
            disabled: false,
            errors: [],
            loading: false,
            onOK: ((data) =>
                close({ ok: true, data })) as UseDialogOKHandler<Output>,
            onCancel,
            options,
        };

        portalSetDialog({
            content: createElement(dialog, props),
            onCancel,
        });
    });
}

export function AsyncDialogPortal() {
    const [activeDialog, setActiveDialog] = useState<ActiveAsyncDialog>();
    const [inactive, setInactive] = useState(false);
    useEffect(() => {
        if (portalSetDialog) {
            setInactive(true);
            return;
        }
        portalSetDialog = setActiveDialog;
        setInactive(false);
        return () => {
            portalSetDialog = null;
        };
    }, []);
    if (inactive) {
        return (
            <>
                ERROR: The AsyncDialogPortal can only be used at one location at
                a time.
            </>
        );
    }
    if (!activeDialog) return null;
    return (
        <Modal onClose={activeDialog.onCancel}>{activeDialog.content}</Modal>
    );
}
